import { useEffect, useMemo, type FC } from 'react';
import { IconButton } from '@carbon/react';
import { ArrowsHorizontal } from '@carbon/react/icons';
import {
  Provider,
  createStore,
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'jotai';
import type { ColorByRule } from 'components/color-by-attribute-modal';
import { CrossSectionBody } from 'components/cross-section/cross-section-body';
import {
  focusedWellUwiAtom,
  gridColorRulesAtom,
  hostCrossingsAtom,
  hostWellColorsAtom,
  hoveredDistanceFtAtom,
  sectionFlippedAtom,
  sectionCutsWithoutCollectionAtom,
  sectionModeAtom,
  sectionVerticesAtom,
  sectionWellWorkspaceIdAtom,
  selectCollectionAtom,
  showIntervalFillsAtom,
  showLocationIndicatorAtom,
  axisZoomEnabledAtom,
  wellColorRulesAtom,
  wellResultsRequestedAtom,
} from 'components/cross-section/atoms';
import {
  INITIAL_GRID_RULE,
  INITIAL_WELL_RULE,
} from 'components/cross-section/cross-section-rail-section';
import { WELL_STATUS_COLORS } from 'components/cross-section/well-colors';
import {
  refreshGridColorRule,
  type GridIdentity,
} from 'components/cross-section/grid-colors';
import { useStructureGridCollection } from 'components/cross-section/use-structure-grid-collections';
import { useCrossSection } from 'components/cross-section/use-cross-section';
import { useCrossSectionRequest } from 'components/cross-section/use-cross-section-request';
import {
  preDrillColorByUwiMapAtom,
  preDrillEditingLineAtom,
  preDrillSectionLineAtom,
  preDrillSectionMarkersAtom,
  type SectionLine,
  type SectionMarkers,
} from './atoms';
import {
  buildColorByUwiColorMap,
  colorForBoreUwi,
  editingCrossings,
} from './utils';
import type { GroupedWell } from 'components/cross-section/utils';
import styles from './workspace-cross-section.module.scss';

// The rail's starting rule with every map status already listed: there is no rail
// here to fill it in, so the section opens on the map's status colours (UAT 36).
const MAP_STATUS_RULE = {
  ...INITIAL_WELL_RULE,
  selectedValues: Object.keys(WELL_STATUS_COLORS),
  valueColors: WELL_STATUS_COLORS,
  derivedFromData: false,
};

export interface WorkspaceCrossSectionProps {
  /** The workspace's structure grid collection, whose grids the section samples. */
  collectionId: number | null;
  /** The Well Workspace whose unpromoted edit of the bore joins the cut as a crossing
   *  of its own (UAT 21). Null outside a Well Workspace. */
  wellWorkspaceId?: number | null;
  /** The bore the sheet is open on, whose unsaved edit the section previews. */
  uwi14: string;
  /** The workspace's colour-by rules, so the section's wells colour as the map's do. */
  colorRules: ColorByRule[];
}

interface SeededCrossSectionProps extends Pick<
  WorkspaceCrossSectionProps,
  'collectionId' | 'wellWorkspaceId'
> {
  /** The map's A–A′ line; null until the bore has two points. */
  sectionLine: SectionLine | null;
  /** Where the map's unsaved edit crosses the line, as crossings; empty when not editing. */
  editingCrossings: GroupedWell[];
  /** The map's colour per well, by ten-digit uwi. */
  wellColors: Record<string, string>;
  /** Told where the section is pointing, so the map can mark the same place. */
  onMarkersChange: (markers: SectionMarkers) => void;
}

/**
 * Seeds the cross-section's atoms from the map and keeps them in step: arbitrary-line
 * mode along A–A′, the collection with all its grids, and the crossed wells. Lives in
 * the isolated store below, so no other cross-section on the page is touched.
 * @param {SeededCrossSectionProps} props The line and the collection.
 * @return {JSX.Element} The section in profile.
 */
const SeededCrossSection: FC<SeededCrossSectionProps> = ({
  sectionLine,
  collectionId,
  wellWorkspaceId = null,
  editingCrossings: previewCrossings,
  wellColors,
  onMarkersChange,
}) => {
  const setMode = useSetAtom(sectionModeAtom);
  const setWellWorkspaceId = useSetAtom(sectionWellWorkspaceIdAtom);
  const setHostCrossings = useSetAtom(hostCrossingsAtom);
  const setHostWellColors = useSetAtom(hostWellColorsAtom);
  const setCutsWithoutCollection = useSetAtom(sectionCutsWithoutCollectionAtom);
  const setVertices = useSetAtom(sectionVerticesAtom);
  const setWellsRequested = useSetAtom(wellResultsRequestedAtom);
  const setShowFills = useSetAtom(showIntervalFillsAtom);
  const setShowIndicator = useSetAtom(showLocationIndicatorAtom);
  const setAxisZoom = useSetAtom(axisZoomEnabledAtom);
  const setGridRules = useSetAtom(gridColorRulesAtom);
  const setWellRules = useSetAtom(wellColorRulesAtom);
  const selectCollection = useSetAtom(selectCollectionAtom);
  const [flipped, setFlipped] = useAtom(sectionFlippedAtom);
  const { grids } = useStructureGridCollection(collectionId);

  useEffect(() => {
    setMode('arbitrary_line');
    // No rail here to switch these on by hand: the crossings are asked for at once and
    // the intervals open filled (UAT 21).
    setWellsRequested(true);
    setShowFills(true);
    setWellRules([MAP_STATUS_RULE]);
    // The section reports where it points from the start, for the map beside it (UAT 36).
    setShowIndicator(true);
    // Dragging an axis gutter rescales that axis, matching the 2D Profile beside it and the
    // Inventory gun barrel chart. Set here, in this section's OWN store, so the Map View's
    // drawer is untouched.
    setAxisZoom(true);
  }, [
    setMode,
    setWellsRequested,
    setShowFills,
    setWellRules,
    setShowIndicator,
    setAxisZoom,
  ]);

  // Without a collection the section still cuts the ground and wells (UAT 30); with one
  // it waits for the grids, or a bare first cut would precede the real one.
  useEffect(() => {
    setCutsWithoutCollection(collectionId === null);
  }, [collectionId, setCutsWithoutCollection]);

  // The sheet's own workspace: the cut carries its unpromoted edit of the bore as a
  // crossing of its own, drawn beside the filed trajectory it corrects.
  useEffect(() => {
    setWellWorkspaceId(wellWorkspaceId);
  }, [wellWorkspaceId, setWellWorkspaceId]);

  // The collection and its whole stack, as the settings panel would pick it, with one
  // colour per interval from the catalogue (without a rule every horizon is blue).
  useEffect(() => {
    if (collectionId === null || grids.length === 0) return;
    selectCollection({
      collectionId,
      gridIds: grids.map((grid) => grid.structureGridId),
      automatic: true,
    });
    const stack: GridIdentity[] = grids.map((grid) => ({
      gridId: String(grid.structureGridId),
      geologicInterval: grid.geologicInterval,
      gridName: grid.gridSurface,
      dataSource: grid.source,
      dataOwner: grid.owner,
      displayColor: grid.geologicIntervalDisplayColor,
    }));
    setGridRules([refreshGridColorRule(INITIAL_GRID_RULE, stack)]);
  }, [collectionId, grids, selectCollection, setGridRules]);

  // What the section is pointing at, handed back so the map can mark the same place
  // (UAT 36). The cut comes from the cache the body already filled, not a second read.
  const hoveredFt = useAtomValue(hoveredDistanceFtAtom);
  const focusedUwi = useAtomValue(focusedWellUwiAtom);
  const { request, generation } = useCrossSectionRequest();
  const { grouped } = useCrossSection(request, generation);
  const focusFt = useMemo(() => {
    if (focusedUwi === null) return null;
    const crossings = [...grouped.wells, ...previewCrossings];
    return crossings.find((well) => well.uwi === focusedUwi)?.xOffsetFt ?? null;
  }, [focusedUwi, grouped.wells, previewCrossings]);
  useEffect(() => {
    onMarkersChange({ hoverFt: hoveredFt, focusFt });
  }, [hoveredFt, focusFt, onMarkersChange]);
  useEffect(
    () => () => onMarkersChange({ hoverFt: null, focusFt: null }),
    [onMarkersChange]
  );

  // The unsaved edit as crossings of its own (UAT 35), and the map's colours for the
  // wells the cut returns (UAT 36) — both the host's to say, neither the cut's.
  useEffect(() => {
    setHostCrossings(previewCrossings);
  }, [previewCrossings, setHostCrossings]);
  useEffect(() => {
    setHostWellColors(wellColors);
  }, [wellColors, setHostWellColors]);

  // A–A′ as the section's two vertices. A drag moves them on every mouse move; the
  // request hook already waits for the plane to settle before it cuts.
  useEffect(() => {
    setVertices(
      sectionLine
        ? [
            { lat: sectionLine.a[1], lon: sectionLine.a[0] },
            { lat: sectionLine.b[1], lon: sectionLine.b[0] },
          ]
        : []
    );
  }, [sectionLine, setVertices]);

  return (
    <div className={styles.host}>
      <div className={styles.toolbar}>
        <IconButton
          kind="ghost"
          size="sm"
          align="left"
          label={flipped ? 'Flipped: A on the right' : 'Flip section direction'}
          onClick={() => setFlipped(!flipped)}
        >
          <ArrowsHorizontal size={16} />
        </IconButton>
      </div>
      <CrossSectionBody />
    </div>
  );
};

/**
 * The Map View's cross-section, cut along the geometry map's A–A′ line (UAT 10) and
 * re-cut as it is dragged. It gets its own store, since its module-level atoms would
 * otherwise share a line with any other cross-section open on the page.
 * @param {WorkspaceCrossSectionProps} props The collection.
 * @return {JSX.Element} The section, in its own store.
 */
export const WorkspaceCrossSection: FC<WorkspaceCrossSectionProps> = ({
  collectionId,
  wellWorkspaceId = null,
  uwi14,
  colorRules,
}) => {
  const sectionLine = useAtomValue(preDrillSectionLineAtom);
  const editingLine = useAtomValue(preDrillEditingLineAtom);
  const colorByUwiMap = useAtomValue(preDrillColorByUwiMapAtom);
  const previewCrossings = useMemo(() => {
    if (!sectionLine || !editingLine) return [];
    return editingCrossings(sectionLine, editingLine, uwi14);
  }, [sectionLine, editingLine, uwi14]);
  // The same resolution the map's layers make: the rules' matches by uwi, and the
  // subject bore's own Well Qualifier colour over them.
  const wellColors = useMemo(() => {
    const colors = buildColorByUwiColorMap(colorRules, colorByUwiMap);
    const subject = colorForBoreUwi(uwi14, uwi14, colorRules, colorByUwiMap);
    if (subject) colors[uwi14.slice(0, 10)] = subject;
    return colors;
  }, [colorRules, colorByUwiMap, uwi14]);
  const setMarkers = useSetAtom(preDrillSectionMarkersAtom);
  const store = useMemo(() => createStore(), []);
  return (
    <Provider store={store}>
      <SeededCrossSection
        sectionLine={sectionLine}
        collectionId={collectionId}
        wellWorkspaceId={wellWorkspaceId}
        editingCrossings={previewCrossings}
        wellColors={wellColors}
        onMarkersChange={setMarkers}
      />
    </Provider>
  );
};
